import { useEffect, useState } from "react";
import { Wifi, WifiOff, RefreshCcw } from "lucide-react";
import { api } from "./api";

const QUEUE_KEY = "hz_checkin_queue";

export const getQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
};

export const setQueue = (q) => {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  window.dispatchEvent(new Event("hz-queue-change"));
};

export const pushQueue = (op) => {
  const q = getQueue();
  q.push(op);
  setQueue(q);
};

// El servidor valida duplicados (idempotencia): reintentos seguros.
export const flushQueue = async () => {
  const q = getQueue();
  if (!q.length) return { done: 0, pending: 0 };
  const remaining = [];
  let done = 0;
  for (const op of q) {
    try {
      await api.post("/checkin/validate", op);
      done++;
    } catch (err) {
      if (err?.response) done++; // respuesta del servidor (duplicado/no autorizado): no reintentar
      else remaining.push(op);
    }
  }
  setQueue(remaining);
  return { done, pending: remaining.length };
};

export const useConnectivity = () => {
  const [status, setStatus] = useState(navigator.onLine ? "online" : "offline");
  const [pending, setPending] = useState(getQueue().length);

  useEffect(() => {
    const onQueue = () => setPending(getQueue().length);
    const onOffline = () => setStatus("offline");
    const onOnline = () => {
      setStatus("reconnecting");
      flushQueue().finally(() => setStatus("online"));
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("hz-queue-change", onQueue);
    const iv = setInterval(() => {
      api.get("/", { timeout: 6000 })
        .then(() => {
          setStatus((s) => {
            if (s === "offline") {
              flushQueue().finally(() => setStatus("online"));
              return "reconnecting";
            }
            return "online";
          });
        })
        .catch(() => setStatus("offline"));
    }, 20000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("hz-queue-change", onQueue);
      clearInterval(iv);
    };
  }, []);

  return { status, pending };
};

const STATUS_UI = {
  online: { cls: "bg-hz-success/10 text-hz-success", icon: Wifi, label: "En línea" },
  offline: { cls: "bg-hz-error/10 text-hz-error", icon: WifiOff, label: "Sin conexión" },
  reconnecting: { cls: "bg-hz-warning/10 text-hz-warning", icon: RefreshCcw, label: "Reconectando" },
};

export const ConnectivityBadge = ({ compact = false }) => {
  const { status, pending } = useConnectivity();
  const ui = STATUS_UI[status];
  const Icon = ui.icon;
  return (
    <span className="inline-flex items-center gap-2" data-testid="connectivity-badge">
      <span className={`inline-flex items-center gap-1.5 rounded-full ${compact ? "px-2 py-1 sm:px-3" : "px-3 py-1"} text-xs font-bold ${ui.cls}`} data-testid={`connectivity-${status}`} title={ui.label} aria-label={ui.label}>
        <Icon className={`h-3.5 w-3.5 ${status === "reconnecting" ? "animate-spin" : ""}`} />
        <span className={compact ? "hidden sm:inline" : ""}>{ui.label}</span>
      </span>
      {pending > 0 && (
        <span className="inline-flex items-center rounded-full bg-hz-warning/15 px-3 py-1 text-xs font-bold text-hz-warning" data-testid="queue-pending-badge">
          {pending} pendiente{pending > 1 ? "s" : ""} de sincronizar
        </span>
      )}
    </span>
  );
};
